import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff, ListChecks, Sparkles, Trophy, Users } from "lucide-react";
import { QUESTION_TYPE_LABEL, type QType } from "@/lib/quiz-grading";

export const Route = createFileRoute("/teacher/quizzes")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Quizzes — MAKAI" }] }),
  component: TeacherQuizzes,
});

const QTYPES: QType[] = ["mcq", "true_false", "fill_blank", "matching", "essay", "short"];
const DIFFS = ["easy", "medium", "hard"] as const;

function TeacherQuizzes() {
  const ctx = useTeacherContext();
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [questionsOf, setQuestionsOf] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    if (!ctx.schoolId) return;
    const { data } = await supabase.from("quizzes")
      .select("id,title,description,difficulty,total_marks,pass_mark,time_limit_min,published,subject_id,stream_id,max_attempts,subject:subjects_catalog(name),stream:streams(name,class:classes(name))")
      .eq("school_id", ctx.schoolId).order("created_at", { ascending: false });
    setQuizzes(data ?? []);
  };
  useEffect(() => { if (!ctx.loading) load(); }, [ctx.loading, ctx.schoolId]);

  async function togglePublish(q: any) {
    const { error } = await supabase.from("quizzes").update({ published: !q.published }).eq("id", q.id);
    if (error) toast.error(error.message); else { toast.success(q.published ? "Unpublished" : "Published"); load(); }
  }
  async function remove(q: any) {
    if (!confirm(`Delete "${q.title}"?`)) return;
    const { error } = await supabase.from("quizzes").delete().eq("id", q.id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); load(); }
  }

  return (
    <AppShell role="teacher" title="Quizzes" subtitle="Build, publish and review quizzes."
      action={<Button onClick={() => setCreating(true)}><Plus className="mr-1 h-4 w-4" /> New quiz</Button>}>
      {quizzes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No quizzes yet. Create your first one to get started.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {quizzes.map((q) => (
            <div key={q.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold uppercase">{q.difficulty}</span>
                    {q.published
                      ? <span className="rounded-md bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">Published</span>
                      : <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold">Draft</span>}
                  </div>
                  <h3 className="mt-2 truncate font-display font-bold">{q.title}</h3>
                  <div className="text-xs text-muted-foreground truncate">
                    {q.subject?.name ?? "All subjects"} · {q.stream?.class?.name ? `${q.stream.class.name} · ${q.stream.name}` : "All classes"}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {q.total_marks} marks · {q.time_limit_min ? `${q.time_limit_min} min` : "Untimed"} · {q.pass_mark != null ? `Pass ${q.pass_mark}%` : "No pass mark"}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                <Button size="sm" variant="outline" onClick={() => setQuestionsOf(q)}><ListChecks className="mr-1 h-3 w-3" /> Questions</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(q)}><Edit className="mr-1 h-3 w-3" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => togglePublish(q)}>{q.published ? <><EyeOff className="mr-1 h-3 w-3" /> Hide</> : <><Eye className="mr-1 h-3 w-3" /> Publish</>}</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(q)}><Trash2 className="h-3 w-3 text-clay" /></Button>
                <AttemptsButton quiz={q} />
              </div>
            </div>
          ))}
        </div>
      )}
      {(creating || editing) && (
        <QuizDialog quiz={editing} open onClose={() => { setCreating(false); setEditing(null); load(); }} ctx={ctx} />
      )}
      {questionsOf && <QuestionsDialog quiz={questionsOf} onClose={() => { setQuestionsOf(null); load(); }} />}
    </AppShell>
  );
}

function AttemptsButton({ quiz }: { quiz: any }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await supabase.from("quiz_attempts")
        .select("id,percent,passed,submitted_at,needs_grading,time_spent_sec,learner:learners(first_name,last_name)")
        .eq("quiz_id", quiz.id).not("submitted_at", "is", null).order("submitted_at", { ascending: false });
      setRows(data ?? []);
    })();
  }, [open, quiz.id]);
  const stats = useMemo(() => {
    if (!rows.length) return null;
    const ps = rows.map((r) => Number(r.percent ?? 0));
    return { n: rows.length, avg: (ps.reduce((a, b) => a + b, 0) / ps.length).toFixed(1), pass: rows.filter((r) => r.passed).length };
  }, [rows]);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}><Users className="mr-1 h-3 w-3" /> Attempts</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{quiz.title} · Attempts</DialogTitle></DialogHeader>
          {stats && (
            <div className="grid grid-cols-3 gap-2">
              <Stat k="Attempts" v={stats.n} />
              <Stat k="Avg %" v={stats.avg} />
              <Stat k="Passed" v={`${stats.pass}/${stats.n}`} />
            </div>
          )}
          {rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No submissions yet.</p> : (
            <div className="overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-xs uppercase text-muted-foreground"><tr><th className="px-3 py-2 text-left">Learner</th><th className="px-3 py-2">%</th><th className="px-3 py-2">Time</th><th className="px-3 py-2">When</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-3 py-2">{r.learner?.first_name} {r.learner?.last_name} {r.needs_grading && <span className="ml-1 rounded bg-gold/20 px-1 text-[10px] text-gold-foreground">grade</span>}</td>
                      <td className="px-3 py-2 text-center font-semibold">{r.percent}%</td>
                      <td className="px-3 py-2 text-center text-xs text-muted-foreground">{r.time_spent_sec ? `${Math.round(r.time_spent_sec/60)}m` : "—"}</td>
                      <td className="px-3 py-2 text-center text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ k, v }: { k: string; v: any }) {
  return <div className="rounded-xl bg-muted p-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">{k}</div><div className="font-display text-lg font-bold">{v}</div></div>;
}

function QuizDialog({ quiz, open, onClose, ctx }: any) {
  const [form, setForm] = useState<any>(quiz ?? {
    title: "", description: "", instructions: "", difficulty: "medium",
    time_limit_min: null, pass_mark: 50, max_attempts: null,
    randomize_questions: false, shuffle_options: false, show_feedback: true, show_correct_answers: true,
    subject_id: ctx.subjects[0]?.id ?? null, stream_id: ctx.streams[0]?.id ?? null,
    available_from: "", available_to: "", published: false, total_marks: 0,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    const payload: any = {
      ...form,
      school_id: ctx.schoolId, created_by: (await supabase.auth.getUser()).data.user?.id,
      time_limit_min: form.time_limit_min ? Number(form.time_limit_min) : null,
      pass_mark: form.pass_mark === "" || form.pass_mark == null ? null : Number(form.pass_mark),
      max_attempts: form.max_attempts ? Number(form.max_attempts) : null,
      available_from: form.available_from || null,
      available_to: form.available_to || null,
      subject_id: form.subject_id || null,
      stream_id: form.stream_id || null,
    };
    const op = quiz ? supabase.from("quizzes").update(payload).eq("id", quiz.id) : supabase.from("quizzes").insert(payload);
    const { error } = await op;
    setSaving(false);
    if (error) toast.error(error.message); else { toast.success("Saved"); onClose(); }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{quiz ? "Edit quiz" : "New quiz"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input placeholder="Title" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea placeholder="Description (optional)" rows={2} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Textarea placeholder="Instructions shown to learners (optional)" rows={2} value={form.instructions ?? ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Sel label="Subject" value={form.subject_id ?? ""} onChange={(v) => setForm({ ...form, subject_id: v })} options={[{ value: "", label: "Any subject" }, ...ctx.subjects.map((s: any) => ({ value: s.id, label: s.name }))]} />
            <Sel label="Class" value={form.stream_id ?? ""} onChange={(v) => setForm({ ...form, stream_id: v })} options={[{ value: "", label: "All classes" }, ...ctx.streams.map((s: any) => ({ value: s.id, label: `${s.class_name} · ${s.name}` }))]} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Sel label="Difficulty" value={form.difficulty} onChange={(v) => setForm({ ...form, difficulty: v })} options={DIFFS.map((d) => ({ value: d, label: d }))} />
            <Field label="Time limit (min)"><Input type="number" value={form.time_limit_min ?? ""} onChange={(e) => setForm({ ...form, time_limit_min: e.target.value })} /></Field>
            <Field label="Pass mark (%)"><Input type="number" value={form.pass_mark ?? ""} onChange={(e) => setForm({ ...form, pass_mark: e.target.value })} /></Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Max attempts"><Input type="number" value={form.max_attempts ?? ""} onChange={(e) => setForm({ ...form, max_attempts: e.target.value })} placeholder="Unlimited" /></Field>
            <Field label="Open from"><Input type="datetime-local" value={form.available_from ?? ""} onChange={(e) => setForm({ ...form, available_from: e.target.value })} /></Field>
            <Field label="Close at"><Input type="datetime-local" value={form.available_to ?? ""} onChange={(e) => setForm({ ...form, available_to: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3 rounded-xl bg-muted/30 p-3">
            <Toggle label="Randomize questions" v={form.randomize_questions} onChange={(b) => setForm({ ...form, randomize_questions: b })} />
            <Toggle label="Shuffle MCQ options" v={form.shuffle_options} onChange={(b) => setForm({ ...form, shuffle_options: b })} />
            <Toggle label="Instant feedback" v={form.show_feedback} onChange={(b) => setForm({ ...form, show_feedback: b })} />
            <Toggle label="Show correct answers" v={form.show_correct_answers} onChange={(b) => setForm({ ...form, show_correct_answers: b })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: any) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}
function Sel({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm capitalize">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}
function Toggle({ label, v, onChange }: { label: string; v: boolean; onChange: (b: boolean) => void }) {
  return <div className="flex items-center justify-between gap-2"><span className="text-sm">{label}</span><Switch checked={!!v} onCheckedChange={onChange} /></div>;
}

function QuestionsDialog({ quiz, onClose }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await supabase.from("quiz_questions").select("*").eq("quiz_id", quiz.id).order("sort_order");
    setItems(data ?? []);
    // recompute total marks
    const total = (data ?? []).reduce((s, q) => s + (q.marks ?? 0), 0);
    await supabase.from("quizzes").update({ total_marks: total }).eq("id", quiz.id);
  };
  useEffect(() => { load(); }, [quiz.id]);

  async function remove(q: any) {
    if (!confirm("Delete this question?")) return;
    await supabase.from("quiz_questions").delete().eq("id", q.id);
    load();
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{quiz.title} · Questions</DialogTitle>
          <p className="text-xs text-muted-foreground">{items.length} questions · {items.reduce((s, q) => s + q.marks, 0)} marks total</p>
        </DialogHeader>
        <div className="space-y-2">
          {items.map((q, i) => (
            <div key={q.id} className="rounded-xl border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase text-muted-foreground">{QUESTION_TYPE_LABEL[q.type as QType]} · {q.marks} mark{q.marks === 1 ? "" : "s"} · <span className="capitalize">{q.difficulty}</span></div>
                  <div className="mt-1 text-sm font-medium">Q{i + 1}. {q.question}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(q)}><Edit className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(q)}><Trash2 className="h-3 w-3 text-clay" /></Button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No questions yet.</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Done</Button>
          <Button onClick={() => setCreating(true)}><Plus className="mr-1 h-4 w-4" /> Add question</Button>
        </DialogFooter>
        {(creating || editing) && (
          <QuestionEditor quizId={quiz.id} sortOrder={items.length}
            question={editing} onClose={() => { setCreating(false); setEditing(null); load(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

export function QuestionEditor({ quizId, sortOrder, question, onClose }: any) {
  const [f, setF] = useState<any>(question ?? {
    type: "mcq", question: "", marks: 1, difficulty: "medium",
    options: ["", "", "", ""], correct_answer: "0", explanation: "",
  });
  // normalize for matching / fill
  useEffect(() => {
    if (question) {
      let opts = question.options;
      let ca = question.correct_answer;
      if (question.type === "matching") { opts = opts ?? { left: [""], right: [""] }; }
      if (question.type === "fill_blank") { try { ca = JSON.parse(ca ?? "[]"); } catch { ca = [""]; } }
      setF({ ...question, options: opts, correct_answer: question.type === "fill_blank" ? ca : question.correct_answer });
    }
  }, [question]);

  async function save() {
    if (!f.question) { toast.error("Question required"); return; }
    const payload: any = {
      quiz_id: quizId, question: f.question, type: f.type,
      marks: Number(f.marks) || 1, difficulty: f.difficulty,
      explanation: f.explanation || null,
      sort_order: question?.sort_order ?? sortOrder,
    };
    if (f.type === "mcq") {
      payload.options = (f.options as string[]).filter((o) => o.trim() !== "");
      payload.correct_answer = String(f.correct_answer ?? "0");
    } else if (f.type === "true_false") {
      payload.options = null;
      payload.correct_answer = f.correct_answer === "true" ? "true" : "false";
    } else if (f.type === "fill_blank") {
      payload.options = null;
      payload.correct_answer = JSON.stringify(Array.isArray(f.correct_answer) ? f.correct_answer : [String(f.correct_answer ?? "")]);
    } else if (f.type === "matching") {
      payload.options = { left: f.options?.left ?? [], right: f.options?.right ?? [] };
      payload.correct_answer = typeof f.correct_answer === "string" ? f.correct_answer : JSON.stringify(f.correct_answer ?? {});
    } else if (f.type === "essay") {
      payload.options = null; payload.correct_answer = null;
    } else if (f.type === "short") {
      payload.options = null;
      payload.correct_answer = String(f.correct_answer ?? "");
    }
    const op = question ? supabase.from("quiz_questions").update(payload).eq("id", question.id) : supabase.from("quiz_questions").insert(payload);
    const { error } = await op;
    if (error) toast.error(error.message); else { toast.success("Saved"); onClose(); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{question ? "Edit question" : "New question"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Sel label="Type" value={f.type} onChange={(raw) => { const v = raw as QType; setF({ ...f, type: v, options: v === "mcq" ? ["", "", "", ""] : v === "matching" ? { left: ["", ""], right: ["", ""] } : null, correct_answer: v === "mcq" ? "0" : v === "true_false" ? "true" : v === "fill_blank" ? [""] : v === "matching" ? "{}" : "" }); }} options={QTYPES.map((t) => ({ value: t, label: QUESTION_TYPE_LABEL[t] }))} />
            <Sel label="Difficulty" value={f.difficulty} onChange={(v: string) => setF({ ...f, difficulty: v })} options={DIFFS.map((d) => ({ value: d, label: d }))} />
            <Field label="Marks"><Input type="number" value={f.marks} onChange={(e) => setF({ ...f, marks: e.target.value })} /></Field>
          </div>
          <Field label="Question"><Textarea rows={2} value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} placeholder={f.type === "fill_blank" ? "Use ___ for each blank" : "Question text"} /></Field>

          {f.type === "mcq" && (
            <div className="space-y-2">
              <Label className="text-xs">Options (select correct)</Label>
              {(f.options as string[]).map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="radio" name="correct" checked={String(f.correct_answer) === String(i)} onChange={() => setF({ ...f, correct_answer: String(i) })} className="accent-primary" />
                  <Input value={o} onChange={(e) => { const opts = [...f.options]; opts[i] = e.target.value; setF({ ...f, options: opts }); }} placeholder={`Option ${i + 1}`} />
                  <Button size="sm" variant="ghost" onClick={() => { const opts = (f.options as string[]).filter((_, j) => j !== i); setF({ ...f, options: opts }); }}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setF({ ...f, options: [...f.options, ""] })}><Plus className="mr-1 h-3 w-3" /> Add option</Button>
            </div>
          )}

          {f.type === "true_false" && (
            <Sel label="Correct answer" value={String(f.correct_answer)} onChange={(v: string) => setF({ ...f, correct_answer: v })} options={[{ value: "true", label: "True" }, { value: "false", label: "False" }]} />
          )}

          {f.type === "fill_blank" && (
            <div className="space-y-2">
              <Label className="text-xs">Correct answers per blank (use "|" for alternatives)</Label>
              {(f.correct_answer as string[]).map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={b} onChange={(e) => { const arr = [...(f.correct_answer as string[])]; arr[i] = e.target.value; setF({ ...f, correct_answer: arr }); }} placeholder={`Blank ${i + 1} (e.g. Kampala|kampala)`} />
                  <Button size="sm" variant="ghost" onClick={() => setF({ ...f, correct_answer: (f.correct_answer as string[]).filter((_, j) => j !== i) })}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setF({ ...f, correct_answer: [...(f.correct_answer as string[]), ""] })}><Plus className="mr-1 h-3 w-3" /> Add blank</Button>
            </div>
          )}

          {f.type === "matching" && (
            <MatchingEditor f={f} setF={setF} />
          )}

          {f.type === "short" && (
            <Field label="Accepted answer (use | for alternatives)"><Input value={f.correct_answer ?? ""} onChange={(e) => setF({ ...f, correct_answer: e.target.value })} /></Field>
          )}

          {f.type === "essay" && (
            <p className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">Essay answers are not auto-marked. You will grade learner submissions manually.</p>
          )}

          <Field label="Explanation (shown after submission)"><Textarea rows={2} value={f.explanation ?? ""} onChange={(e) => setF({ ...f, explanation: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MatchingEditor({ f, setF }: any) {
  const left: string[] = f.options?.left ?? [];
  const right: string[] = f.options?.right ?? [];
  let map: Record<string, string> = {};
  try { map = typeof f.correct_answer === "string" ? JSON.parse(f.correct_answer || "{}") : (f.correct_answer ?? {}); } catch { map = {}; }
  function setMap(nextMap: Record<string, string>) { setF({ ...f, correct_answer: JSON.stringify(nextMap) }); }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Left items</Label>
          {left.map((v, i) => (
            <div key={i} className="mt-1 flex gap-1"><Input value={v} onChange={(e) => { const arr = [...left]; arr[i] = e.target.value; setF({ ...f, options: { ...f.options, left: arr } }); }} /></div>
          ))}
          <Button size="sm" variant="outline" className="mt-1" onClick={() => setF({ ...f, options: { ...f.options, left: [...left, ""] } })}><Plus className="mr-1 h-3 w-3" /> Add</Button>
        </div>
        <div>
          <Label className="text-xs">Right items</Label>
          {right.map((v, i) => (
            <div key={i} className="mt-1 flex gap-1"><Input value={v} onChange={(e) => { const arr = [...right]; arr[i] = e.target.value; setF({ ...f, options: { ...f.options, right: arr } }); }} /></div>
          ))}
          <Button size="sm" variant="outline" className="mt-1" onClick={() => setF({ ...f, options: { ...f.options, right: [...right, ""] } })}><Plus className="mr-1 h-3 w-3" /> Add</Button>
        </div>
      </div>
      <div>
        <Label className="text-xs">Correct matches</Label>
        <div className="mt-1 space-y-1">
          {left.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <div className="rounded bg-muted px-2 py-1 text-sm">{l || `(left ${i + 1})`}</div>
              <span>→</span>
              <select value={map[i] ?? ""} onChange={(e) => setMap({ ...map, [i]: e.target.value })} className="rounded border border-input bg-background px-2 py-1 text-sm">
                <option value="">—</option>
                {right.map((r, j) => <option key={j} value={String(j)}>{r || `(right ${j + 1})`}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
